import axios from 'axios'
import packageJson from '../../package.json'
import useLocalStorage from './useLocalStorage'
import useAuth from './auth'

import router from '../router'
import useLang from './lang'

export default function useAxios() {
  const { t } = useI18n()
  const notification = useNotification()
  const { jwt, rememberMe, resetUseStorageItems } = useLocalStorage()
  const { refreshToken } = useAuth()
  const { defaultLang } = useLang()

  const setupAxios = () => {
    axios.defaults.baseURL = import.meta.env.VITE_API_BASEURL
    axios.defaults.params = {
      locale: defaultLang.value
    }
    axios.interceptors.request.use(
      function (config) {
        if (jwt.value !== null) {
          config.headers['Authorization'] = `Bearer ${jwt.value}`
        }
        config.headers['X-App-Version'] = packageJson.version
        config.headers['X-App-Platform'] = 'dashboard'

        if (import.meta.env.VITE_API_BASEURL.includes('ngrok')) {
          config.headers['ngrok-skip-browser-warning'] = 'true'
        }

        return config
      },
      function (error) {
        return Promise.reject(error)
      }
    )
    axios.interceptors.response.use(
      function (response) {
        return response
      },
      async function (error) {
        const originalRequest = error.config
        if (error.response?.status === 401) {
          if (
            error.config.url.includes('partner/auth') ||
            /partners\/\d+\//.test(error.config.url) ||
            error.config.url.includes('stores-from-partner') ||
            error.config.url.includes('accounts') ||
            error.config.url.includes('devices')
          ) {
            notification.create('error', {
              text: t('global.partner.notifications.errors.partnerAuth'),
              persist: true,
              action: {
                label: t('global.partner.notifications.errors.partnerAuthAction'),
                onClick: () => {
                  router.push({ name: 'IntegrationList' })
                }
              }
            })
            return Promise.reject(error)
          }
          if (!rememberMe.value) {
            resetUseStorageItems()
            router.push({
              name: 'Login'
            })
          }
          if (rememberMe.value && !originalRequest._retry) {
            originalRequest._retry = true
            try {
              await refreshToken()
            } catch {
              resetUseStorageItems()
              router.push({
                name: 'Login'
              })
              return Promise.reject(error)
            }

            originalRequest.headers['Authorization'] = `Bearer ${jwt.value}`
            return axios.request(originalRequest)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  return { setupAxios }
}
